<template>
  <div class="content container">
    <!-- Iteracja po wszystkich pojazdach -->
    <div v-for="vehicle in vehicles" :key="vehicle.id" class="vehicles-list">
      <!-- Panel pojazdu; obsługa kliknięcia prawym przyciskiem myszy (context menu) -->
      <div class="row vehicle-panel" @contextmenu.prevent="onRightClick(vehicle)">
        <div class="image">
          <!-- Komponent wyświetlający zdjęcie pojazdu -->
          <vehicle-image
            :vehicle="vehicle"
            :image-service="vehService"
            :source="'vehicle-list'"
          ></vehicle-image>
        </div>
        <!-- Kliknięcie przenosi do szczegółów pojazdu -->
        <div class="measurement-row" @click="handleGoToDetails(vehicle)">
          <div class="row align-items-center">
            <!-- Nazwa lokalizacji (stacji) -->
            <div class="col-4 measurement-row-header vehicle-header">{{ vehicle.location.shortName }}</div>
            <!-- Flaga i numer rejestracyjny -->
            <div class="col-4 plate-item" id="vehicle-flag">
              <div class="row measurement-row-header">
                <!-- Flaga kraju pochodzenia -->
                <div class="col-2 plate-flag center-aligned" :style="{ backgroundColor: getCode(vehicle.nationality) ? '' : 'white'}">
                  <div class="country-flag">
                    <country-flag :country="getCode(vehicle.nationality)" size="small" />
                  </div>
                  <div class="country-code">
                    <span>{{ getCode(vehicle.nationality) ? getCode(vehicle.nationality) : "" }}</span>
                  </div>
                </div>
                <!-- Numer rejestracyjny -->
                <div class="col-8 plate">{{ getPlate(vehicle) }}</div>
              </div>
            </div>
            <!-- Data i godzina rejestracji -->
            <div class="col-2 measurement-row-header-thin center-aligned">
              {{ getFormattedDate(vehicle.createdAt) }}
            </div>
            <div class="col-2 measurement-row-header-thin center-aligned">
              {{ getFormattedTime(vehicle.createdAt) }}
            </div>
          </div>
          <!-- Kategoria pojazdu -->
          <div class="row measurement-row-row">
            <div class="col-8 measurement-row-field vehicle-header-2">{{ t('category') }} [8+1]</div>
            <div :class="'col-4 measurement-row-field-b field-center field-icon center-aligned' + getCategoryClass(vehicle.ctg8p1)">
              {{ vehicle.ctg8p1 }}
            </div>
          </div>
          <!-- Prędkość pojazdu -->
          <div class="row measurement-row-row">
            <div class="col-8 measurement-row-field vehicle-header-2">{{ t('speed') }} [km/h]</div>
            <div :class="'col-4 field-center center-aligned ' + getSpeedClassName(vehicle)">
              {{ vehicle.speed }} km/h
            </div>
          </div>
          <!-- Masa całkowita pojazdu -->
          <div class="row measurement-row-row">
            <div class="col-8 measurement-row-field vehicle-header-2">
              {{ t('total-weight') }} [limit {{ vehicle.limitTotalLoad }} kg]
            </div>
            <div :class="'col-4 field-center center-aligned ' + getWeightClassName(vehicle)">
              {{ vehicle.totalLoad }} kg
            </div>
          </div>
        </div>
        <!-- Panel osi pojazdu i timer -->
        <div class="load" @click="handleGoToDetails(vehicle)">
          <div class="row measurement-row-header">
            <div class="items-centered center-aligned top-pad" style="display: flex; align-items: center; gap: 50px; margin-left: 5px;">
              <!-- Timer do checkpointu -->
              <!-- <CountdownTimer
                :deadline="moment(vehicle.checkpointTime).format('YYYY/MM/DD HH:mm:ss')"
              /> -->
              <CountdownTimer
                :deadline="moment(vehicle.checkpointTime).format('YYYY/MM/DD HH:mm:ss')"
                :server-diff="momentDiffServer"
                :obj-number="String(vehicle.id)"
              />
              <div style="display: flex; align-items: center; gap: 8px;">
                <span style="padding-bottom: 5px;">{{ t('axle-load').toUpperCase() }} [t]</span>
                <!-- Zaznaczenie wybranego pojazdu (np. po dodaniu taga) -->
                <span v-if="selectedVehicleIds.includes(vehicle.id)" class="checkmark" style="padding-bottom: 5px;">✔</span>
              </div>
            </div>
          </div>
          <!-- Wiersz z pierwszą grupą osi (jeśli istnieje) -->
          <div class="row measurement-row-body centered-flex" v-if="existsAxlesRow1(vehicle.axles)">
            <div class="col-1 axle-1">
              <div class="row-cols-3 axle-field">{{ t('axle') }}:</div>
              <div class="row-cols-6">
                <div class="icon-a"></div>
              </div>
              <div class="row-cols-3 axle-field">t:</div>
            </div>
            <div v-for="ax in getAxlesRow1(vehicle.axlesComplex.axlesGroups)" :key="ax" :class="['col-1', 'width width-' + ax.type]" v-html="getDivs(ax)">
            </div>
          </div>
          <!-- Wiersz z kolejnymi osiami (jeśli są, np. powyżej 7 osi) -->
          <div v-if="existsAxlesRow2(vehicle.axles)" class="row" id="axles8to14">
            <div class="col-1">
              <div class="row-cols-3 axle-field">{{ t('axle') }}:</div>
              <div class="row-cols-6">
                <div class="icon-a"></div>
              </div>
              <div class="row-cols-3 axle-field">t:</div>
            </div>
            <div v-for="ax in getAxlesRow2(vehicle.axles)" :key="ax" class="col-1">
              <div class="items-centered-axles center-aligned">
                <div :class="'row-cols-3 ' + getAxisNumberClass(ax)">
                  {{ ax.type }}
                </div>
                <div class="row-cols-6">
                  <div :class="'icon-a ' + getAxisClass(ax)"></div>
                </div>
                <div :class="'row-cols-3 ' + getAxisNumberClass(ax)">
                  {{ ax.totalLoad }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// Importy bibliotek i komponentów
import moment from "moment";
import CountryFlag from "vue-country-flag-next";
import getCountryISO2 from "country-iso-3-to-2";
import VehicleImage from "@/components/VehicleImage";
import CountdownTimer from "@/components/current/countdown-timer";
import ArchivalMeasurementsService from "@/services/ArchivalMeasurementsService";
import { useUserStore } from "@/stores/user.store";

moment.locale("pl");

export default {
  props: {
    vehicleList: { required: true },
    dataService: { required: true },
    detailsPath: { required: true },
    // momentDiffServer: { default: 0 },
    timerVisible: { default: true },
    goToDetailsView: { type: Function },
    isFromTags: { type: Boolean, default: false },
  },
  name: "VehicleList",
  components: {
    CountdownTimer,
    VehicleImage,
    CountryFlag,
  },
  watch: {
    // Nasłuchiwanie zmian na liście pojazdów
    vehicleList: {
      handler(newVal) {
        this.vehicles = newVal;
        // Jeśli wyświetlamy pojazdy po tagowaniu wszystkie są "zaznaczone"
        if (this.isFromTags) {
          this.selectedVehicleIds = newVal.map(vehicle => vehicle.id);
        }
        // const now = Date.now();

        // // 1) indeksujemy wejściową listę po id
        // const incomingById = new Map(newVal.map(v => [v.id, v]));

        // // 2) bierzemy z obecnej listy te pojazdy, które:
        // //    - zniknęły z wejścia (rodzic ich już nie wysyła),
        // //    - ale ich lokalny czas (deadline + serverDiff) jeszcze nie minął
        // const retained = (this.vehicles || []).filter(
        //   v => !incomingById.has(v.id) && now < this.getEndTs(v)
        // );

        // // 3) łączymy: nowe dane od rodzica + „do-trzymania”
        // //    (brak duplikatów, bo 'retained' zawiera tylko ID nieobecne w newVal)
        // this.vehicles = [...newVal, ...retained];

        // // tagi zostaw jak było
        // if (this.isFromTags) {
        //   this.selectedVehicleIds = this.vehicles.map(vehicle => vehicle.id);
        // }
      },
      immediate: true,
      deep: false,
    }
  },
  data() {
    return {
      moment: moment,
      vehService: this.dataService,
      vehicles: this.vehicleList,
      selectedVehicleIds: [],
      vehicleTags: {},
      userStore: useUserStore(),
      momentDiffServer: 0,
    };
  },
  async mounted() {
    // pobierz preferencje użytkownika i ustaw momentDiffServer
    await this.userStore.self();
    const addSec = this.userStore?.preferences?.checkpointAdditionalTime || 0; // sekundy
    this.momentDiffServer = Number(addSec) * 1000; // na ms
  },
  methods: {
    // Obsługa kliknięcia PPM: dodaje lub usuwa tag z pojazdu
    async onRightClick(vehicle) {
      const id = Number(vehicle.id);

      if (this.selectedVehicleIds.includes(id)) {
        // Usuwanie znacznika, jeśli pojazd już jest oznaczony
        const tagId = this.vehicleTags[id] || vehicle.tags[0].id;
        if (tagId) {
          try {
            await ArchivalMeasurementsService.removeTag(id, tagId, vehicle.location.id, this.getCreatedAt(vehicle));
            delete this.vehicleTags[id];
            this.selectedVehicleIds = this.selectedVehicleIds.filter(vId => vId !== id);
          } catch (e) {
            console.error("Błąd podczas usuwania znacznika:", e);
          }
        }
      } else {
        // Dodawanie znacznika, jeśli pojazd nie jest oznaczony
        try {
          const response = await ArchivalMeasurementsService.addTag(
            id,
            vehicle.location.id,
            this.getCreatedAt(vehicle)
          );
          const tagId = response?.id;
          if (tagId) {
            this.vehicleTags[id] = tagId;
            this.selectedVehicleIds = [...this.selectedVehicleIds, id];
          } else {
            console.warn("Brak ID w odpowiedzi z addTag:", response);
          }
        } catch (e) {
          console.error("Błąd podczas dodawania znacznika:", e);
        }
      }
    },
    // Obsługa przejścia do szczegółów pojazdu
    handleGoToDetails(vehicle) {
      console.log(this.goToDetailsView);
      if (this.goToDetailsView) {
        this.goToDetailsView(vehicle);
      } else {
        this.goToDetailsViewInternal(vehicle);
      }
    },
    // Domyślne przejście do szczegółów pojazdu (przekierowanie przez router)
    goToDetailsViewInternal(vehicle) {
      const path = this.detailsPath.includes("vehicleArch")
        ? `/${this.detailsPath}/${vehicle.id}/${vehicle.location.id}/${this.getCreatedAt(vehicle)}`
        : `/${this.detailsPath}/${vehicle.id}/${vehicle.location.id}`;
      this.$router.push({ path });
    },
    // getEndTs(vehicle) {
    //   // vehicle.checkpointTime masz w ISO/Date – moment to ogarnie
    //   const cpMs = this.moment(vehicle.checkpointTime).valueOf();
    //   return cpMs + Number(this.momentDiffServer || 0);
    // },
    // onTimerEnd({ id }) {
    //   const numericId = Number(id);
    //   this.vehicles = this.vehicles.filter(v => v.id !== numericId);
    //   this.selectedVehicleIds = this.selectedVehicleIds.filter(vId => vId !== numericId);
    //   if (this.vehicleTags[numericId]) delete this.vehicleTags[numericId];
    //   this.$emit('removeVehicle', { id: numericId });
    // },
    // Zwraca kod ISO2 kraju (np. "PL", "DE")
    getCode(value) {
      const fl = getCountryISO2(value);
      return fl || "";
    },
    // Zwraca klasę do stylowania w zależności od przekroczenia masy
    getWeightClassName(vehicle) {
      if (vehicle.totalLoad >= vehicle.limitTotalLoad) {
        return "measurement-row-field-b-red";
      }
      return "measurement-row-field-b";
    },
    // Zwraca klasę do stylowania w zależności od przekroczenia prędkości
    getSpeedClassName(vehicle) {
      if (vehicle.overspeed) {
        return "measurement-row-field-b-red";
      }
      return "measurement-row-field-b";
    },
    // Kategoria pojazdu (klasa z ikoną)
    getCategoryClass(ctg8p1) {
      return " icon-" + ctg8p1;
    },
    // Czy istnieją dane o osiach (pierwszy rząd)
    existsAxlesRow1(axles) {
      return axles;
    },
    getAxlesRow1(axles) {
      if (axles) {
        return axles;
      }
    },
    // Czy są kolejne osie (więcej niż 7)
    existsAxlesRow2(axles) {
      return axles && axles.size > 7;
    },
    getAxlesRow2(axles) {
      if (axles && axles.size > 7) {
        return axles.subarray(7);
      }
      return [];
    },
    // Zwraca klasę CSS ikony osi
    getAxisClass(axis) {
      if (!axis || !axis.type) return "";

      const baseClass = "axis-icon";
      const typeSuffix = axis.type > 1 ? `-${axis.type}` : "";
      const overloadSuffix = axis.overload ? "-ov" : "";

      const iconClass = "icon-a";
      const typeIconSuffix = axis.type > 1 ? `-${axis.type}` : "";
      const overloadIconSuffix = axis.overload ? "-ov" : "";

      return `${iconClass}${typeIconSuffix}${overloadIconSuffix} ${baseClass}${overloadSuffix}${typeSuffix}`;
    },
    // Zwraca klasę do wyróżnienia osi z przeciążeniem
    getAxisNumberClass(axis) {
      if (axis && axis.overload) {
        return "axle-field-red";
      }
      return "axle-field";
    },
    // Zwraca numer rejestracyjny (jeśli brak, "-----")
    getPlate(vehicle) {
      return vehicle.plateLpr || "-----";
    },
    // Zwraca datę w formacie ISO
    getCreatedAt(vehicle) {
      return this.moment(vehicle.createdAt).toISOString(false);
    },
    // Formatowanie daty/godziny dla UI
    getFormattedDate(obj) {
      return moment(obj).add(this.momentDiffServer, "milliseconds").format("DD.MM.YYYY");
    },
    getFormattedTime(obj) {
      return moment(obj).add(this.momentDiffServer, "milliseconds").format("HH:mm:ss");
    },
    getFormattedDateTime(obj) {
      return moment(obj).add(this.momentDiffServer, "milliseconds").format("DD.MM.YYYY HH:mm:ss");
    },
    // Emitowanie usunięcia pojazdu do rodzica
    removeVehicle(id) {
      return this.$emit("removeVehicle", { id: id.id });
    },
    // Generowanie html do prezentacji osi w pierwszym rzędzie (grupy osi)
    getDivs(ax) {
      let divs = '';

      if (ax.type == 2 || ax.type == 3) {
        let totalLoad = (ax.totalLoad / 1000).toFixed(2);
        divs += `
          <div class="d-flex flex-column">
            <div class="row-cols-3 ${this.getAxisNumberClass(ax)} center-aligned">
              ${ax.type}
            </div>
            <div class="row-cols-6 center-aligned">
              <div class="${this.getAxisClass(ax)}"></div>
            </div>
            <div class="row-cols-3 ${this.getAxisNumberClass(ax)} center-aligned">
              ${totalLoad}
            </div>
          </div>`;
      } else {
        divs += `<div class="d-flex flex-rows">`;
        ax.axles.forEach((singleAxle) => {
          let singleTotalLoad = (singleAxle.totalLoad / 1000).toFixed(2);

          divs += `
            <div class="d-flex flex-column align-items-center ${this.getWidthValue(singleAxle)}" style="margin-right: 8px;">
              <div class="row-cols-3 ${this.getAxisNumberClass(singleAxle)} center-aligned">
                ${singleAxle.type > 3 ? "W" : singleAxle.type}
              </div>
              <div class="row-cols-6 center-aligned">
                <div class="${this.getAxisClass(singleAxle)}"></div>
              </div>
              <div class="row-cols-3 ${this.getAxisNumberClass(singleAxle)} center-aligned">
                ${singleTotalLoad}
              </div>
            </div>`;
        });
        divs += `</div>`;
      }

      return divs;
    },
    // Szerokość dla klasy css na podstawie typu osi
    getWidthValue(ax) {
      if(ax.type == 4) {
        return `mr-8`;
      }
      return '';
    }
  },
};
</script>

<style scoped>
.checkmark {
  color: green;
  font-size: 18px;
  font-weight: bold;
  margin-left: 60px;
}

.measurement-row {
  border-right: 1px solid #e1e1df;
  background: rgb(242, 240, 238);
  width: 50%;
  height: auto;
}

.measurement-row-header {
  font-weight: bold;
  font-size: 16px;
  background-color: white;
  color: black;
  height: 35px;
  padding: 5px 5px 0 5px;
}

.measurement-row-header-thin {
  font-weight: normal;
  font-size: 14px;
  background-color: white;
  border-left: 1px outset #cccccc;
  color: black;
  height: 35px;
  padding: 7px 5px 0 5px !important;
}

.measurement-row-row {
  font-size: 12px;
  padding: 8px 0;
  border-bottom: 1px solid #b8b8b6;
  height: 26%;
}

.top-pad {
  padding-top: 5px !important;
}

.measurement-row-body {
  /* width: 100%; */
  height: 118px;
  background: rgb(242, 240, 238);
  overflow-x: auto;
  white-space: nowrap;
  /* display: flex; */
  /* padding: 8px 0; */
}

.measurement-row-row.field-form {
  border-bottom: 0;
  padding: 8px 0 8px;
}

.measurement-row-field {
  padding-left: 0.25rem;
  padding-top: 2px;
}

.measurement-row-field-b {
  font-weight: bold;
  font-size: 14px;
  padding-top: 10px !important;
}

.measurement-row-field-b-red {
  font-weight: bold;
  font-size: 14px;
  color: red;
  padding-top: 10px !important;
}

.width {
  align-items: center;
  margin-right: 8px !important;
}

.width-1 {
  width: 30px;
}

.width-2 {
  width: 60px;
}

.width-3 {
  width: 90px;
}

.width-4 {
  width: 140px;
}

.mr-8 {
  margin-right: 8px;
}

.field-icon {
  background-position-x: 80px;
  text-indent: -25px;

}

.vehicle-panel {
  width: 100%;
  margin-left: 0;
}

.vehicle-panel .image {
  padding: 0;
  width: 20%;
}

.load {
  width: 30%;
}

.centered-flex {
  display: flex;
  justify-content: center;
  align-items: center;
}

.plate-flag {
  background: #204499;
  color: #f5f4f2;
  font-weight: bold;
  font-size: 12px;
  width: 45px;
  height: 35px;
}

.plate-flag .country-flag {
  height: 17.5px;
}

.plate {
  background: white;
  color: black;
  font-weight: bold;
  font-size: 21px;
  height: 35px;
  padding: 1px 5px !important;
}

.plate-item {
  height: 35px;
}

.plate-item .measurement-row-header {
  padding-top: 0;
}

.axle-row .col-1 {
  padding: 0 15px;
}

.axle-row .col-1:first-child {
  padding: 0;
}

.axle-field {
  font-size: 12px;
  background: rgb(242, 240, 238);
}

.axle-field-red {
  font-weight: bold;
  font-size: 12px;
  background: rgb(242, 240, 238);
  color: red;
}

.count2,
.count3,
.count4,
.count5 {
  margin-top: 25px;
}

.count6,
.count7,
.count8,
.count9,
.count10 {
  margin-top: 25px;
}

.with-margin-8 {
  margin-right: 8px;
}

.items-centered-axles {
  width: 30px;
}

.vehicles-list {
  width: 100%;
  margin: 0 auto;
  border-bottom: 1px solid #e1e1df;
}

.vehicles-list:first-child {
  border-top: 1px solid #e1e1df;
}

.measurement-row-row:last-child {
  border-bottom: none;
}

.row {
    margin: 0;
    padding: 0;
    display: flex;
}

.row > * {
    margin: 0;
    padding: 0;
}

.vehicle-header {
  padding: 5px 0 0 5px;
}

.vehicle-header-2 {
  padding: 10px 0 0 5px;
}

.image-icon {
  height: 20px;
}
</style>
